// Tramos de descuento por volumen para tarjetas regalo de empresa (admin).
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Plus, Edit2, Trash2, Loader2, AlertCircle, X } from 'lucide-react';

import {
  listTramosEmpresa, createTramoEmpresa, updateTramoEmpresa, deleteTramoEmpresa, simularDescuentoEmpresa,
} from '../../lib/adminApi';

const VACIO = { nombre: '', desde_euros: '', descuento_pct: '', activo: true };
const eur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;

export default function AdminTramosEmpresa() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);
  const [valorSim, setValorSim] = useState('1000');
  const [sim, setSim] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems((await listTramosEmpresa()).data || []); }
    catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const v = Number(valorSim);
    if (!(v > 0)) { setSim(null); return undefined; }
    const h = setTimeout(() => { simularDescuentoEmpresa(v).then(setSim).catch(() => setSim(null)); }, 300);
    return () => clearTimeout(h);
  }, [valorSim, items]);

  const alternar = async (x) => { try { await updateTramoEmpresa(x.id, { activo: !x.activo }); cargar(); } catch (e) { alert(e.message); } };
  const borrar = async (x) => {
    if (!window.confirm(t('admin.tramos_empresa.confirmar_borrar'))) return;
    try { await deleteTramoEmpresa(x.id); cargar(); } catch (e) { alert(e.message); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.tramos_empresa.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.tramos_empresa.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}
      {!loading && !error && items.length === 0 && <p className="mw-vacio">{t('admin.tramos_empresa.vacio')}</p>}

      {!loading && !error && items.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.tramos_empresa.col.nombre')}</th>
              <th>{t('admin.tramos_empresa.col.desde')}</th>
              <th>{t('admin.tramos_empresa.col.descuento')}</th>
              <th>{t('admin.tramos_empresa.col.estado')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id}>
                <td>{x.nombre || '—'}</td>
                <td>{eur(x.desde_euros)}</td>
                <td><strong>{Number(x.descuento_pct)} %</strong></td>
                <td>
                  <button type="button" className={'estado-pill mw-pill-btn' + (x.activo ? ' estado-pill--ok' : '')} onClick={() => alternar(x)}>
                    {x.activo ? t('admin.tramos_empresa.activo') : t('admin.tramos_empresa.inactivo')}
                  </button>
                </td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar({ ...x, nombre: x.nombre || '' })} title={t('admin.editar')}><Edit2 size={16} /></button>
                  <button type="button" onClick={() => borrar(x)} title={t('admin.tramos_empresa.borrar')}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section className="admin-card" style={{ marginTop: 24, maxWidth: 520 }}>
        <h2 className="admin-card-titulo">{t('admin.tramos_empresa.simulador')}</h2>
        <div className="modal-campo">
          <label>{t('admin.tramos_empresa.simulador_valor')}</label>
          <input type="number" min="1" step="1" value={valorSim} onChange={(e) => setValorSim(e.target.value)} />
        </div>
        {sim && (
          <p className="mw-sub">
            {t('admin.tramos_empresa.simulador_res', {
              pct: sim.descuento_pct, ahorro: eur(sim.descuento_euros), pagar: eur(sim.a_pagar),
            })}
          </p>
        )}
      </section>

      {editar && <TramoModal inicial={editar} onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />}
    </div>
  );
}

function TramoModal({ inicial, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [f, setF] = useState(inicial);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState(null);
  const editando = !!inicial.id;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setGuardando(true); setErr(null);
    const body = {
      nombre: f.nombre, desde_euros: Number(f.desde_euros), descuento_pct: Number(f.descuento_pct), activo: f.activo,
    };
    try {
      if (editando) await updateTramoEmpresa(inicial.id, body); else await createTramoEmpresa(body);
      onGuardado();
    } catch (e2) { setErr(e2); setGuardando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !guardando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.tramos_empresa.nuevo')}</h2>
        <form className="modal-form" onSubmit={submit}>
          <div className="modal-campo full">
            <label>{t('admin.tramos_empresa.col.nombre')}</label>
            <input type="text" maxLength={80} value={f.nombre} onChange={set('nombre')} />
          </div>
          <div className="modal-campo">
            <label>{t('admin.tramos_empresa.f.desde')}</label>
            <input type="number" min="1" step="0.01" required value={f.desde_euros} onChange={set('desde_euros')} />
          </div>
          <div className="modal-campo">
            <label>{t('admin.tramos_empresa.f.descuento')}</label>
            <input type="number" min="0.01" max="100" step="0.01" required value={f.descuento_pct} onChange={set('descuento_pct')} />
          </div>
          <div className="modal-campo full">
            <label><input type="checkbox" checked={f.activo} onChange={set('activo')} /> {t('admin.tramos_empresa.activo')}</label>
          </div>
          {err && <div className="modal-error-general">{err.message}</div>}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={guardando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={guardando}>
              {guardando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
